import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { loadUserStats } from 'actions/user';
import { toggleRightAside } from 'actions/ui';
import BlobImage from 'components/blobImage';
import { SystemText } from '@salesperformanceint/e4shared';
import { addMinutes, isPast } from 'helpers/dates';
import { hasModule } from 'helpers/profile';
import PartnerSpoof from '../partnerspoof';
import Signout from 'components/general/signout';

export const Navigation = ({ history, props }) => {
    const dispatch = useDispatch();

    const [menuOpen, setMenuOpen] = React.useState('');

    const { user, stats, statsLoading, statsLastLoaded } = useSelector(state => state.user);
    const outer = React.useRef();

    React.useEffect(() => {
        document.addEventListener('mousedown', handleClick, false);

        if (!stats || isPast(addMinutes(statsLastLoaded, 1))) {
            dispatch(loadUserStats());
        }

        return () => {
            document.removeEventListener('mousedown', handleClick, false);
        };
    }, []);

    React.useEffect(() => {
        dispatch(loadUserStats());
    }, [user?.id]);

    const toggleMenu = menuToToggle => {
        setMenuOpen(menuOpen === menuToToggle ? '' : menuToToggle);
    };

    const toggleRightAsideWrapper = () => {
        dispatch(toggleRightAside());
        toggleMenu('');
    };

    const handleClick = e => {
        if (!outer.current?.contains(e.target)) {
            toggleMenu('');
        }
    };

    const hasObjectives = hasModule(user, 'objectives');

    const upcomingTasks =
        stats && hasObjectives
            ? stats.upcomingTasks.map(n => ({ ...n, url: `/objectives/${n.profile_Id}/objective/${n.id}` }))
            : [];
    const overdueTasks =
        stats && hasObjectives
            ? stats.overdueTasks.map(n => ({ ...n, url: `/objectives/${n.profile_Id}/objective/${n.id}` }))
            : [];
    const teamOverdueTasks =
        stats && hasObjectives
            ? stats.teamOverdueTasks.map(n => ({ ...n, url: `/objectives/${n.profile_Id}?filter=overdue` }))
            : [];
    const teamAwaitingTasks =
        stats && hasObjectives
            ? stats.teamAwaitingTasks.map(n => ({ ...n, url: `/objectives/${n.profile_Id}?filter=awaiting` }))
            : [];

    const text = {
        period: {
            yesterday: SystemText('Header/Periods/Yesterday'),
            today: SystemText('Header/Periods/Today'),
            tomorrow: SystemText('Header/Periods/Tomorrow'),
            past: x => SystemText('Header/Periods/Past', x),
            future: x => SystemText('Header/Periods/Future', x),
        },
        nav: {
            objectives: {
                label: SystemText('Header/Nav/Objectives/Label'),
                desc: x => SystemText('Header/Nav/Objectives/Desc', x),
                due: SystemText('Header/Nav/Objectives/Due'),
                all: SystemText('Header/Nav/Objectives/All'),
            },
            overdue: {
                label: SystemText('Header/Nav/Overdue/Label'),
                desc: x => SystemText('Header/Nav/Overdue/Desc', x),
                due: SystemText('Header/Nav/Overdue/Due'),
                all: SystemText('Header/Nav/Overdue/All'),
            },
            team: {
                label: SystemText('Header/Nav/Team/Label'),
                desc: x => SystemText('Header/Nav/Team/Desc', x),
                due: x => SystemText('Header/Nav/Team/Due', x),
                all: SystemText('Header/Nav/Team/All'),
            },
            approval: {
                label: SystemText('Header/Nav/Approval/Label'),
                desc: x => SystemText('Header/Nav/Approval/Desc', x),
                due: x => SystemText('Header/Nav/Approval/Due', x),
                all: SystemText('Header/Nav/Approval/All'),
            },
        },
    };

    const inXDays = days => {
        switch (days) {
            case -1:
                return text.period.yesterday;
            case 0:
                return text.period.today;
            case 1:
                return text.period.tomorrow;
            default:
                return days > 0 ? text.period.past(days) : text.period.future(days * -1);
        }
    };

    const clickOnly = (e, callback) => {
        e.preventDefault();
        callback();
    };

    return (
        <>
            <div className="navbar-custom-menu">
                <ul className="nav navbar-nav" ref={outer}>
                    <li>
                        <PartnerSpoof />
                    </li>

                    {upcomingTasks && upcomingTasks.length > 0 && (
                        <li
                            id="alerts-upcoming"
                            className={`hidden-xs dropdown tasks-menu ${menuOpen === 'tasks' ? 'open' : ''}`}
                        >
                            <a
                                tabIndex="30"
                                href="#objectives"
                                title={text.nav.objectives.label}
                                onClick={e => clickOnly(e, () => toggleMenu('tasks'))}
                            >
                                <i className="fa fa-flag" />
                                <span className="label label-warning">
                                    {statsLoading ? <i className="fa fa-spinner fa-spin" /> : upcomingTasks.length}
                                </span>
                            </a>
                            <ul className="dropdown-menu">
                                <li className="header">{text.nav.objectives.desc(upcomingTasks.length)}</li>
                                <li>
                                    <ul className="menu">
                                        {upcomingTasks?.map(task => {
                                            return (
                                                <li key={`upcoming-${task.id}`}>
                                                    <Link to={task.url}>
                                                        <h3>{task.name}</h3>
                                                        <div>
                                                            <small className="text-black">
                                                                {text.nav.objectives.due} {inXDays(task.dueIn)}
                                                            </small>
                                                        </div>
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                                <li className="footer">
                                    <Link tabIndex="30" to="/objectives">
                                        {text.nav.objectives.all}
                                    </Link>
                                </li>
                            </ul>
                        </li>
                    )}
                    {overdueTasks && overdueTasks.length > 0 && (
                        <li
                            id="alerts-overdue"
                            className={`dropdown tasks-menu ${menuOpen === 'overdue-tasks' ? 'open' : ''}`}
                        >
                            <a
                                tabIndex="30"
                                href="#overdue"
                                title={text.nav.overdue.label}
                                onClick={e => clickOnly(e, () => toggleMenu('overdue-tasks'))}
                            >
                                <i className="fa fa-warning" />

                                <span className="label label-danger">
                                    {statsLoading ? <i className="fa fa-spinner fa-spin" /> : overdueTasks.length}
                                </span>
                            </a>
                            <ul className="dropdown-menu">
                                <li className="header">{text.nav.overdue.desc(overdueTasks.length)}</li>
                                <li>
                                    <ul className="menu">
                                        {overdueTasks?.map(task => {
                                            return (
                                                <li key={`overdue-${task.id}`}>
                                                    <Link to={task.url}>
                                                        <h3>{task.name}</h3>
                                                        <div>
                                                            <small className="text-black">
                                                                {text.nav.objectives.due} {inXDays(task.dueIn)}
                                                            </small>
                                                        </div>
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                                <li className="footer">
                                    <Link tabIndex="30" to="/objectives">
                                        {text.nav.objectives.all}
                                    </Link>
                                </li>
                            </ul>
                        </li>
                    )}
                    {teamOverdueTasks && teamOverdueTasks.length > 0 && (
                        <li
                            id="alerts-team-overdue"
                            className={`dropdown tasks-menu ${menuOpen === 'team-tasks' ? 'open' : ''}`}
                        >
                            <a
                                tabIndex="30"
                                href="#overdueteam"
                                title={text.nav.team.label}
                                onClick={e => clickOnly(e, () => toggleMenu('team-tasks'))}
                            >
                                <i className="fa fa-users" />

                                <span className="label label-danger">
                                    {statsLoading ? <i className="fa fa-spinner fa-spin" /> : teamOverdueTasks.length}
                                </span>
                            </a>
                            <ul className="dropdown-menu">
                                <li className="header">{text.nav.team.desc(teamOverdueTasks.length)}</li>
                                <li>
                                    <ul className="menu">
                                        {teamOverdueTasks?.map(task => {
                                            return (
                                                <li key={`team-overdue-${task.profile_Id}`}>
                                                    <Link tabIndex="30" to={task.url}>
                                                        <h3>{`${task.first_Name} ${
                                                            task.last_Name ? task.last_Name.slice(0, 1) : ''
                                                        }.`}</h3>
                                                        <div>
                                                            <small className="text-black">
                                                                {text.nav.team.due(task.count)}
                                                            </small>
                                                        </div>
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            </ul>
                        </li>
                    )}
                    {teamAwaitingTasks && teamAwaitingTasks.length > 0 && (
                        <li
                            id="alerts-awaiting"
                            className={`hidden-xs dropdown tasks-menu ${menuOpen === 'team-awaiting' ? 'open' : ''}`}
                        >
                            <a
                                tabIndex="30"
                                href="#awaitingapproval"
                                title={text.nav.approval.label}
                                onClick={e => clickOnly(e, () => toggleMenu('team-awaiting'))}
                            >
                                <i className="fa fa-thumbs-up" />

                                <span className="label label-warning">
                                    {statsLoading ? <i className="fa fa-spinner fa-spin" /> : teamAwaitingTasks.length}
                                </span>
                            </a>
                            <ul className="dropdown-menu">
                                <li className="header">{text.nav.approval.desc(teamAwaitingTasks.length)}</li>
                                <li>
                                    <ul className="menu">
                                        {teamAwaitingTasks?.map(task => {
                                            return (
                                                <li key={`awaiting-${task.profile_Id}`}>
                                                    <Link tabIndex="30" to={task.url}>
                                                        <h3>{`${task.first_Name} ${
                                                            task.last_Name ? task.last_Name.slice(0, 1) : ''
                                                        }.`}</h3>
                                                        <div>
                                                            <small className="text-black">
                                                                {text.nav.approval.due(task.count)}
                                                            </small>
                                                        </div>
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            </ul>
                        </li>
                    )}
                    <li className={`dropdown user user-menu ${menuOpen === 'user' ? 'open' : ''}`}>
                        <a
                            tabIndex="30"
                            data-testid="a-header-user"
                            href="#profile"
                            onClick={e => clickOnly(e, () => toggleMenu('user'))}
                        >
                            <BlobImage
                                folder="avatar"
                                initials={user.initials}
                                key={user.avatar}
                                src={user.avatar}
                                className="img-square"
                                style={{
                                    width: '25px',
                                    height: '25px',
                                    float: 'left',
                                    margin: '-2px 10px 0 0',
                                    border: 'solid 0px',
                                }}
                            />
                            <span className="hidden-xs">{user?.full_Name}</span>
                        </a>
                        <ul className="dropdown-menu" data-testid="ul-header-user">
                            <Signout history={history} />
                        </ul>
                    </li>
                    {user.sys_Config && (
                        <li>
                            <a
                                tabIndex="30"
                                href="#admin"
                                aria-label="Open admin menu"
                                id="toggle-right-button"
                                onClick={e => clickOnly(e, () => toggleRightAsideWrapper())}
                                data-toggle="control-sidebar"
                            >
                                <i className="fa fa-gears" id="toggle-right-button-icon" />
                            </a>
                        </li>
                    )}
                </ul>
            </div>
        </>
    );
};

export default withRouter(Navigation);
